import React, { useEffect, useRef, useState } from 'react';
import { Dna, Upload, Trash2, ChevronDown, ChevronUp, Link as LinkIcon, ShieldCheck, ShoppingCart, Download, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 1.5 * 1024 * 1024;
const GENRES = ['House', 'Techno', 'Trance', 'Drum & Bass', 'Bass', 'Breaks & Garage', 'Hard Dance', 'Electro & Synth', 'Ambient & Downtempo', 'Experimental'];
const TYPES = [['edit', 'Edit'], ['bootleg', 'Bootleg'], ['remix', 'Remix'], ['mashup', 'Mashup'], ['vip', 'VIP'], ['rework', 'Rework'], ['flip', 'Flip'], ['extended', 'Extended'], ['dub', 'Dub']];
const TYPE_LABEL = Object.fromEntries(TYPES);
const REFERENCE_TYPES = ['original_mix', 'curated_id'];
const isReference = (e) => e.entry_kind === 'curated' || REFERENCE_TYPES.includes(e.version_type);
const fmtDur = (s) => {
  const n = Math.round(Number(s) || 0);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

const MAX_BATCH = 25;
const deriveTitle = (name) => name.replace(/\.[^.]+$/, '').replace(/_+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 140);
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('beatseek_token')}` });

const uploadInChunks = async (file, onProgress) => {
  const uploadId = Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, '0')).join('');
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append('upload_id', uploadId);
    fd.append('index', String(i));
    fd.append('total', String(total));
    fd.append('chunk', file.slice(i * CHUNK, (i + 1) * CHUNK), `part_${i}`);
    const r = await fetch(`${API}/api/community-index/upload/chunk`, { method: 'POST', headers: authHeaders(), body: fd });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `Upload failed (${r.status})`);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return uploadId;
};

// Inline "drop the download link" editor for a registered edit (mirrors onto its Wall post)
const EditLinkEditor = ({ edit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(edit.link || '');
  const [status, setStatus] = useState(edit.release_status || 'unreleased');
  const save = async () => {
    const r = await fetch(`${API}/api/community-index/${edit.id}`, { method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ link, release_status: status }) });
    if (r.ok) { toast.success(link ? 'Link dropped — listeners can grab it from the Wall' : 'Link removed'); setOpen(false); onSaved?.(); } else toast.error('Could not save link');
  };
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} data-testid={`edit-link-toggle-${edit.id}`} style={{ display: 'block', marginTop: 4, background: 'none', border: 0, padding: 0, color: '#f7d55c', fontSize: '0.74rem', cursor: 'pointer' }}>{edit.link ? '🔗 Update download link' : '🔗 Drop a download link'}</button>;
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }} data-testid={`edit-link-editor-${edit.id}`}>
      <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://soundcloud.com/… or download link" data-testid={`edit-link-input-${edit.id}`} style={{ flex: 1, minWidth: 180, padding: '6px 9px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: '0.78rem' }} />
      <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '6px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: '0.78rem' }}>
        <option value="unreleased">Unreleased</option><option value="free_download">Free download</option><option value="forthcoming">Forthcoming</option><option value="released">Released</option>
      </select>
      <button type="button" onClick={save} data-testid={`edit-link-save-${edit.id}`} style={{ padding: '6px 12px', borderRadius: 999, border: '1px solid rgba(247,213,92,0.7)', background: 'rgba(247,213,92,0.15)', color: '#f7d55c', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>Save</button>
      <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 0, color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', cursor: 'pointer' }}>Cancel</button>
    </div>
  );
};

// Register unreleased edits/bootlegs so the BEATSEEK ID engine can recognise them (fingerprints only, audio discarded).
export const RegisterEditPanel = ({ isLoggedIn, prefill }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(false);
  const [mine, setMine] = useState([]);
  const [stats, setStats] = useState({ edits: 0, matches: 0 });
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(0);
  const [form, setForm] = useState({ entry_kind: '', title: '', original_artist: '', original_title: '', version_type: '', genre: 'House', subgenres: '', link: '', buy_link: '', free_dl_link: '', release_status: 'unreleased', attestation: false });
  const [progress, setProgress] = useState(null);
  const [phase, setPhase] = useState(null);
  const fileRef = useRef(null);
  const panelRef = useRef(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/community-index/mine`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setMine(d.edits || []); setStats(d.my_stats || d.stats || stats); }
    } catch (_) {}
  };
  useEffect(() => { if (isLoggedIn) load(); }, [open, isLoggedIn]);

  // Funnel from an ID result: open the panel, prefill the original, scroll it into view.
  useEffect(() => {
    if (!prefill) return;
    setOpen(true);
    setForm(f => ({ ...f, original_artist: prefill.original_artist || f.original_artist, original_title: prefill.original_title || f.original_title }));
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    // eslint-disable-next-line
  }, [prefill?.at]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const isRef = form.entry_kind === 'curated';
  const batch = files.length > 1;
  const patchRow = (i, p) => setFiles(prev => prev.map((r, ix) => (ix === i ? { ...r, ...p } : r)));

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const ok = chosen.filter(f => f.size <= 80 * 1024 * 1024);
    const over = chosen.length - ok.length;
    if (over) toast.error(`${over} file${over > 1 ? 's' : ''} over 80 MB skipped — export 320kbps MP3s`);
    if (ok.length > MAX_BATCH) { toast.error(`${MAX_BATCH} files max per batch — the rest were left out`); ok.length = MAX_BATCH; }
    setFiles(ok.map(f => ({ f, title: deriveTitle(f.name), state: 'idle', pct: 0, error: null })));
    setDone(0);
    if (ok.length === 1) setForm(fm => ({ ...fm, title: fm.title || deriveTitle(ok[0].name) }));
  };

  const registerOne = async (fileObj, title) => {
    const uploadId = await uploadInChunks(fileObj, (p) => setProgress(p));
    setPhase('index');
    const fd = new FormData();
    fd.append('upload_id', uploadId); fd.append('filename', fileObj.name);
    Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
    fd.set('title', title);
    fd.set('version_type', form.version_type || (isRef ? 'curated_id' : 'original'));
    const r = await fetch(`${API}/api/community-index/register`, { method: 'POST', headers: authHeaders(), body: fd });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Indexing failed');
    return d.edit;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error('Choose your audio file first');
    if (!form.entry_kind) return toast.error('Pick Original or Curated in the first dropdown');
    if (!batch && !form.title.trim()) return toast.error('Give your edit a title');
    if (batch && files.some(r => !r.title.trim())) return toast.error('Every track in the batch needs a title');
    if (!form.attestation) return toast.error(isRef ? 'Please confirm the details are accurate' : 'Please confirm you created these');
    if (isRef && !form.original_artist.trim()) return toast.error('Enter the correct artist so we can name it properly');

    setDone(0);
    let okCount = 0;
    const failures = [];
    for (let i = 0; i < files.length; i++) {
      const row = files[i];
      setPhase('upload'); setProgress(0);
      patchRow(i, { state: 'upload', error: null });
      try {
        const edit = await registerOne(row.f, batch ? row.title : form.title);
        patchRow(i, { state: 'done' });
        okCount += 1;
        setDone(okCount);
        if (!batch) {
          toast.success(isRef
            ? `"${edit.title}" is in for review — once a moderator approves it, BEATSEEK will name it`
            : `"${edit.title}" is now in the Community Edit Index — BEATSEEK can ID it from now on`);
        }
      } catch (err) {
        patchRow(i, { state: 'error', error: err.message });
        failures.push(`${row.f.name}: ${err.message}`);
      }
      setPhase(null); setProgress(null);
    }

    if (batch) {
      if (okCount) {
        toast.success(`${okCount} of ${files.length} indexed${isRef ? ' — in for review' : ''}`,
          { description: failures.length ? `${failures.length} failed — see the list below` : 'BEATSEEK can ID these from now on' });
      } else {
        toast.error('Nothing was indexed', { description: failures[0] });
      }
    } else if (failures.length) {
      toast.error(failures[0].split(': ').slice(1).join(': '));
    }

    if (!failures.length) {
      setFiles([]); if (fileRef.current) fileRef.current.value = '';
      setForm({ ...form, title: '', original_artist: '', original_title: '', subgenres: '', link: '', buy_link: '', free_dl_link: '', attestation: false });
    }
    load();
  };

  const remove = async (id) => {
    const r = await fetch(`${API}/api/community-index/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) { toast.success('Edit removed from the index'); load(); } else toast.error('Could not remove');
  };

  return (
    <div className="edit-index-panel" ref={panelRef} data-testid="community-edit-panel">
      <button type="button" className="edit-index-toggle" onClick={() => setOpen(!open)} data-testid="community-edit-toggle">
        <span className="edit-index-head">
          <Dna size={18} />
          <span className="edit-index-title">Register an Edit</span>
          {stats.edits > 0 && <span className="edit-index-stat" data-testid="community-edit-stats">your {stats.edits} {stats.edits === 1 ? 'entry' : 'entries'} · {stats.matches} {stats.matches === 1 ? 'ID' : 'IDs'}</span>}
          <span
            className="edit-index-info"
            role="button"
            tabIndex={0}
            title="What is the Community Edit Index?"
            data-testid="community-edit-info"
            onClick={(e) => { e.stopPropagation(); setInfo(v => !v); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setInfo(v => !v); } }}
          >
            <Info size={14} />
          </span>
          <span className="edit-index-chev">{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
        </span>
        <span className="edit-index-sub">
          <span>Your edits, bootlegs &amp; originals — or the correct info for a track we named wrong.</span>
          <span>BEATSEEK learns the fingerprint and IDs it properly next time.</span>
        </span>
      </button>
      {info && (
        <p className="edit-index-sub-mobile" data-testid="community-edit-info-text">
          Two uses. <strong>1.</strong> Register your own tracks, edits &amp; bootlegs so BEATSEEK can ID them when they get
          played — no other app can. <strong>2.</strong> Fix the database: if we named a released track wrong or don't know
          it at all, upload it with the accurate info and we'll ID it correctly from then on. Curated IDs never overwrite
          the original result — they sit alongside it, credited to you. Only the audio fingerprint is stored; the file is deleted.
        </p>
      )}
      {open && (
        <div className="edit-index-body">
          {!isLoggedIn ? (
            <p className="edit-index-note">Log in to register your edits.</p>
          ) : (
            <form onSubmit={submit} className="edit-index-form" data-testid="community-edit-form">
              <label className={`edit-index-drop ${files.length ? 'has-file' : ''}`} data-testid="community-edit-file-label">
                <Upload size={18} />
                <span>{files.length === 1
                  ? `${files[0].f.name} · ${(files[0].f.size / 1048576).toFixed(1)} MB`
                  : files.length > 1
                    ? `${files.length} tracks ready to index · ${(files.reduce((a, r) => a + r.f.size, 0) / 1048576).toFixed(1)} MB total`
                    : (isRef ? "Choose the track's audio file — or pick several at once (MP3 / WAV / AIFF / M4A · 80 MB each)" : 'Choose your edits — pick one or a whole folder at once (MP3 / WAV / AIFF / M4A · 80 MB each)')}</span>
                <input ref={fileRef} type="file" multiple accept="audio/*,.mp3,.wav,.aiff,.aif,.m4a,.flac" onChange={pickFiles} data-testid="community-edit-file" />
              </label>
              {batch && (
                <div className="edit-index-batch" data-testid="community-edit-batch">
                  <p className="edit-index-batch-head">
                    Titles come from the filenames — tweak any of them. Everything below applies to all {files.length}.
                  </p>
                  {files.map((r, i) => (
                    <div key={`${r.f.name}-${i}`} className={`edit-index-batch-row state-${r.state}`} data-testid={`community-edit-batch-row-${i}`}>
                      <span className="edit-index-batch-state">
                        {r.state === 'done' ? '✓' : r.state === 'error' ? '!' : r.state === 'upload' || r.state === 'index' ? '…' : i + 1}
                      </span>
                      <input
                        value={r.title}
                        onChange={(e) => patchRow(i, { title: e.target.value })}
                        placeholder="Track title"
                        disabled={!!phase}
                        data-testid={`community-edit-batch-title-${i}`}
                      />
                      {r.error
                        ? <span className="edit-index-batch-err" data-testid={`community-edit-batch-error-${i}`}>{r.error}</span>
                        : <span className="edit-index-batch-size">{(r.f.size / 1048576).toFixed(1)} MB</span>}
                      <button type="button" onClick={() => setFiles(prev => prev.filter((_, ix) => ix !== i))} disabled={!!phase} aria-label="Remove from batch" data-testid={`community-edit-batch-remove-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="edit-index-kinds">
                <select value={form.entry_kind} onChange={set('entry_kind')} data-testid="community-edit-kind" aria-label="Entry kind">
                  <option value="">(select)</option>
                  <option value="original">Original</option>
                  <option value="curated">Curated</option>
                </select>
                <select value={form.version_type} onChange={set('version_type')} data-testid="community-edit-type" aria-label="Type">
                  <option value="">Type</option>
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {!batch && (
                <div className="edit-index-grid">
                  <input className="edit-index-full" value={form.title} onChange={set('title')} placeholder={isRef ? 'Correct track title · e.g. Glue' : 'Edit title · e.g. Glue (Your Name Bootleg)'} required data-testid="community-edit-title" />
                </div>
              )}
              <div className="edit-index-grid">
                <input value={form.original_artist} onChange={set('original_artist')} placeholder={isRef ? 'Correct artist · e.g. Bicep' : 'Original artist (optional)'} data-testid="community-edit-orig-artist" />
                <input value={form.original_title} onChange={set('original_title')} placeholder={isRef ? 'Label / release (optional)' : 'Original title (optional)'} data-testid="community-edit-orig-title" />
                <select value={form.genre} onChange={set('genre')} data-testid="community-edit-genre">
                  {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <input value={form.subgenres} onChange={set('subgenres')} placeholder="Subgenres, comma separated (optional)" data-testid="community-edit-subgenres" />
                <div className="edit-index-link"><LinkIcon size={14} /><input value={form.link} onChange={set('link')} placeholder="SoundCloud / download link (optional)" data-testid="community-edit-link" /></div>
                <select value={form.release_status} onChange={set('release_status')} data-testid="community-edit-status">
                  <option value="unreleased">Unreleased</option><option value="free_download">Free download</option><option value="forthcoming">Forthcoming</option><option value="released">Released</option>
                </select>
                <div className="edit-index-link"><ShoppingCart size={14} /><input value={form.buy_link} onChange={set('buy_link')} placeholder="Where to buy it · Beatport / Bandcamp (optional)" data-testid="community-edit-buy-link" /></div>
                <div className="edit-index-link"><Download size={14} /><input value={form.free_dl_link} onChange={set('free_dl_link')} placeholder="Free download link (optional)" data-testid="community-edit-free-link" /></div>
              </div>
              <label className="edit-index-attest" data-testid="community-edit-attest">
                <input type="checkbox" checked={form.attestation} onChange={set('attestation')} />
                <ShieldCheck size={14} /> {isRef
                  ? "The details I've entered are accurate and I'm not claiming authorship of this track. Only its audio fingerprint is stored — the file is deleted after indexing."
                  : `I made ${batch ? 'these tracks' : 'this track or edit'} and I'm registering ${batch ? 'them' : 'it'} under my own name. Only the audio fingerprint is stored — the ${batch ? 'files are' : 'file is'} deleted after indexing.`}
              </label>
              <button type="submit" className="edit-index-submit" disabled={!!phase} data-testid="community-edit-submit">
                {phase && batch ? `Indexing ${Math.min(done + 1, files.length)} of ${files.length}… ${phase === 'upload' ? `${progress}%` : 'fingerprinting'}`
                  : phase === 'upload' ? `Uploading… ${progress}%`
                  : phase === 'index' ? 'Fingerprinting…'
                  : batch ? `Register ${files.length} ${isRef ? 'IDs' : 'edits'}`
                  : (isRef ? 'Register this ID' : 'Register edit')}              </button>
              {phase && <div className="edit-index-progress"><div style={{ width: `${phase === 'index' ? 100 : progress}%` }} /></div>}
            </form>
          )}
          {mine.length > 0 && (
            <div className="edit-index-mine" data-testid="community-edit-mine">
              <p className="edit-index-mine-title">Your index entries</p>
              {mine.map(e => (
                <div key={e.id} className="edit-index-item" data-testid="community-edit-item">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <strong>{e.title}{e.review_status === 'pending' && <span data-testid="community-edit-pending" style={{ marginLeft: 8, color: '#f7d55c', fontSize: '0.7rem', fontWeight: 700 }}>⏳ AWAITING REVIEW</span>}{e.review_status === 'rejected' && <span data-testid="community-edit-rejected" style={{ marginLeft: 8, color: '#ff6b6b', fontSize: '0.7rem', fontWeight: 700 }}>NOT APPROVED</span>}{(e.request_count || 0) > 0 && <span data-testid="community-edit-demand" style={{ marginLeft: 8, color: '#ff7aa8', fontSize: '0.75rem' }}>🔥 {e.request_count} want this</span>}</strong>
                    <span>{(isReference(e) ? 'CURATED' : 'ORIGINAL')}{TYPE_LABEL[e.version_type] ? ` · ${TYPE_LABEL[e.version_type].toUpperCase()}` : ''} · {e.genre}{e.original_artist ? ` · ${isReference(e) ? 'by' : 'based on'} ${e.original_artist}${e.original_title ? ` – ${e.original_title}` : ''}` : ''} · {fmtDur(e.duration_s)} · IDs: {e.match_count || 0}</span>
                    {(e.buy_link || e.free_dl_link) && (
                      <span style={{ display: 'block', marginTop: 2 }}>
                        {e.buy_link && <a href={e.buy_link} target="_blank" rel="noreferrer" style={{ color: '#f7d55c', marginRight: 10 }}>Buy</a>}
                        {e.free_dl_link && <a href={e.free_dl_link} target="_blank" rel="noreferrer" style={{ color: '#00ff88' }}>Free download</a>}
                      </span>
                    )}
                    <EditLinkEditor edit={e} onSaved={load} />
                  </div>
                  <button type="button" onClick={() => remove(e.id)} aria-label="Remove edit" data-testid="community-edit-delete"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegisterEditPanel;
